import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, apiService } from '../services/api';
import Layout from '../components/Layout';
import GridLayout from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { 
  Plus, 
  Save, 
  ArrowLeft, 
  Pencil, 
  Trash2, 
  Loader2,
  GripVertical,
  BarChart3,
  LineChartIcon,
  PieChartIcon,
  Table,
  Hash,
  X,
  Sparkles
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const CHART_COLORS = ['#4F46E5', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899'];

const WidgetComponent = ({ widget, data, onDelete, editMode }) => {
  const renderContent = () => {
    if (!data || data.length === 0) {
      return (
        <div className="flex items-center justify-center h-full text-slate-400 text-sm">
          Sem dados disponíveis
        </div>
      );
    }

    const config = widget.config_json || {};
    const xKey = config.x_axis || Object.keys(data[0])[0];
    const yKey = config.y_axis || Object.keys(data[0])[1] || Object.keys(data[0])[0];

    switch (widget.type) {
      case 'kpi':
        const value = data[0]?.[yKey] || data[0]?.[Object.keys(data[0])[0]] || 0;
        return (
          <div className="flex flex-col items-center justify-center h-full">
            <span className="text-4xl font-bold text-indigo-600 tabular-nums">
              {typeof value === 'number' ? value.toLocaleString('pt-BR', { maximumFractionDigits: 2 }) : value}
            </span>
            <span className="text-sm text-slate-500 mt-2">{config.metric || yKey}</span>
          </div>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                }} 
              />
              <Bar dataKey={yKey} fill="#4F46E5" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        );

      case 'line':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey={xKey} tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <YAxis tick={{ fontSize: 12 }} stroke="#94A3B8" />
              <Tooltip 
                contentStyle={{ 
                  backgroundColor: 'white', 
                  border: '1px solid #E2E8F0',
                  borderRadius: '8px',
                  boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)'
                }} 
              />
              <Line type="monotone" dataKey={yKey} stroke="#4F46E5" strokeWidth={2} dot={{ fill: '#4F46E5' }} />
            </LineChart>
          </ResponsiveContainer>
        );

      case 'pie':
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={data}
                dataKey={yKey}
                nameKey={xKey}
                cx="50%"
                cy="50%"
                outerRadius={80}
                label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                labelLine={false}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        );

      case 'table':
        const columns = Object.keys(data[0] || {});
        return (
          <div className="overflow-auto h-full">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  {columns.map(col => (
                    <th key={col} className="px-3 py-2 text-left font-medium text-slate-600 border-b">
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.slice(0, 50).map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    {columns.map(col => (
                      <td key={col} className="px-3 py-2 border-b border-slate-100 tabular-nums">
                        {typeof row[col] === 'number' 
                          ? row[col].toLocaleString('pt-BR', { maximumFractionDigits: 2 })
                          : row[col]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      default:
        return <div className="flex items-center justify-center h-full text-slate-400">Tipo não suportado</div>;
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-slate-50/50">
        {editMode && (
          <div className="cursor-move mr-2">
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        )}
        <h3 className="font-medium text-slate-800 flex-1 truncate">{widget.title}</h3>
        {editMode && (
          <button 
            onClick={() => onDelete(widget.id)}
            className="p-1 hover:bg-red-50 rounded text-slate-400 hover:text-red-500"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        )}
      </div>
      <div className="flex-1 p-4 min-h-0">
        {renderContent()}
      </div>
    </div>
  );
};

const DashboardBuilderPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { token } = useAuth();
  
  const [dashboard, setDashboard] = useState(null);
  const [widgets, setWidgets] = useState([]);
  const [widgetData, setWidgetData] = useState({});
  const [extractions, setExtractions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(searchParams.get('edit') === 'true');
  const [layout, setLayout] = useState([]);
  
  // Add Widget Dialog
  const [addWidgetOpen, setAddWidgetOpen] = useState(false);
  const [newWidget, setNewWidget] = useState({
    title: '',
    type: 'bar',
    extraction_id: '',
    config_json: {}
  });

  const fetchData = useCallback(async () => {
    try {
      const [dashResponse, extractionsResponse] = await Promise.all([
        apiService.getDashboard(id, token),
        apiService.getExtractions(token)
      ]);
      
      setDashboard(dashResponse.data);
      setExtractions(extractionsResponse.data);
      
      // Fetch widgets
      const widgetsResponse = await apiService.getWidgets(id, token);
      setWidgets(widgetsResponse.data);
      
      // Set layout from dashboard or generate default
      const savedLayout = dashResponse.data.layout_json?.lg || [];
      if (savedLayout.length > 0) {
        setLayout(savedLayout);
      } else {
        // Generate default layout for widgets
        const defaultLayout = widgetsResponse.data.map((w, i) => ({
          i: w.id,
          x: (i % 2) * 6,
          y: Math.floor(i / 2) * 4,
          w: 6,
          h: 4,
          minW: 2,
          minH: 2
        }));
        setLayout(defaultLayout);
      }
      
      // Fetch data for each widget
      const dataPromises = widgetsResponse.data.map(async (widget) => {
        if (widget.extraction_id) {
          try {
            const result = await apiService.runExtraction(widget.extraction_id, {}, token);
            return { id: widget.id, data: result.data.data };
          } catch {
            return { id: widget.id, data: [] };
          }
        }
        return { id: widget.id, data: [] };
      });
      
      const dataResults = await Promise.all(dataPromises);
      const dataMap = {};
      dataResults.forEach(r => { dataMap[r.id] = r.data; });
      setWidgetData(dataMap);
      
    } catch (error) {
      toast.error('Erro ao carregar dashboard');
      navigate('/dashboards');
    } finally {
      setLoading(false);
    }
  }, [id, token, navigate]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleLayoutChange = (newLayout) => {
    setLayout(newLayout);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await apiService.updateDashboard(id, {
        name: dashboard.name,
        layout_json: { lg: layout }
      }, token);
      toast.success('Dashboard salvo!');
    } catch (error) {
      toast.error('Erro ao salvar dashboard');
    } finally {
      setSaving(false);
    }
  };

  const handleAddWidget = async () => {
    if (!newWidget.title || !newWidget.extraction_id) {
      toast.error('Preencha todos os campos obrigatórios');
      return;
    }

    try {
      const response = await apiService.createWidget(id, newWidget, token);
      const createdWidget = response.data;
      
      // Add to layout
      const newLayoutItem = {
        i: createdWidget.id,
        x: 0,
        y: Math.max(...layout.map(l => l.y + l.h), 0),
        w: 6,
        h: 4,
        minW: 2,
        minH: 2
      };
      setLayout([...layout, newLayoutItem]);
      
      // Fetch widget data
      if (createdWidget.extraction_id) {
        try {
          const result = await apiService.runExtraction(createdWidget.extraction_id, {}, token);
          setWidgetData(prev => ({ ...prev, [createdWidget.id]: result.data.data }));
        } catch {
          setWidgetData(prev => ({ ...prev, [createdWidget.id]: [] }));
        }
      }
      
      setWidgets([...widgets, createdWidget]);
      setAddWidgetOpen(false);
      setNewWidget({ title: '', type: 'bar', extraction_id: '', config_json: {} });
      toast.success('Widget adicionado!');
    } catch (error) {
      toast.error('Erro ao adicionar widget');
    }
  };

  const handleDeleteWidget = async (widgetId) => {
    try {
      await apiService.deleteWidget(widgetId, token);
      setWidgets(widgets.filter(w => w.id !== widgetId));
      setLayout(layout.filter(l => l.i !== widgetId));
      toast.success('Widget removido');
    } catch (error) {
      toast.error('Erro ao remover widget');
    }
  };

  const widgetTypes = [
    { value: 'kpi', label: 'KPI', icon: Hash },
    { value: 'bar', label: 'Barras', icon: BarChart3 },
    { value: 'line', label: 'Linha', icon: LineChartIcon },
    { value: 'pie', label: 'Pizza', icon: PieChartIcon },
    { value: 'table', label: 'Tabela', icon: Table },
  ];

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-indigo-600 animate-spin" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/dashboards')} className="shrink-0">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="min-w-0">
              <h1 className="font-heading text-2xl font-semibold text-slate-900 truncate" data-testid="dashboard-title" title={dashboard?.name}>
                {dashboard?.name}
              </h1>
              <p className="text-slate-500 text-sm mt-0.5">
                {widgets.length} widget{widgets.length !== 1 ? 's' : ''}
              </p>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
            {editMode ? (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setAddWidgetOpen(true)}
                  className="w-full sm:w-auto flex justify-center"
                  data-testid="add-widget-btn"
                >
                  <Plus className="w-4 h-4 mr-2 shrink-0" />
                  Adicionar Widget
                </Button>
                <Button 
                  onClick={handleSave}
                  disabled={saving}
                  className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto flex justify-center"
                  data-testid="save-dashboard-btn"
                >
                  {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin shrink-0" /> : <Save className="w-4 h-4 mr-2 shrink-0" />}
                  Salvar
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)} className="w-full sm:w-auto flex justify-center text-slate-500">
                  <X className="w-4 h-4 mr-2 shrink-0" />
                  Fechar
                </Button>
              </>
            ) : (
              <Button variant="outline" onClick={() => setEditMode(true)} className="w-full sm:w-auto flex justify-center" data-testid="edit-dashboard-btn">
                <Pencil className="w-4 h-4 mr-2 shrink-0" />
                Editar Dashboard
              </Button>
            )}
          </div>
        </div>

        {/* Dashboard Grid */}
        {widgets.length === 0 ? (
          <div className="text-center py-10 sm:py-20 bg-white rounded-xl border border-slate-200 border-dashed px-4">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 flex items-center justify-center">
              <BarChart3 className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-heading text-lg font-medium text-slate-900 mb-2">
              Dashboard vazio
            </h3>
            <p className="text-slate-500 mb-6">
              Adicione widgets para visualizar seus dados
            </p>
            <div className="flex flex-col sm:flex-row items-stretch justify-center gap-3 w-full sm:w-auto max-w-sm mx-auto">
              <Button 
                onClick={() => setAddWidgetOpen(true)}
                className="bg-indigo-600 hover:bg-indigo-700 w-full"
              >
                <Plus className="w-4 h-4 mr-2 shrink-0" />
                Adicionar Widget
              </Button>
              <Button 
                variant="outline"
                onClick={() => navigate('/extractions?will=true')}
                className="border-violet-300 text-violet-600 hover:bg-violet-50 w-full"
              >
                <Sparkles className="w-4 h-4 mr-2 shrink-0" />
                Criar com WILL
              </Button>
            </div>
          </div>
        ) : (
          <div className={`min-h-[600px] ${editMode ? 'dashboard-grid-bg rounded-xl p-4' : ''}`}>
            <GridLayout
              className="layout"
              layout={layout}
              cols={12}
              rowHeight={80}
              width={1200}
              isDraggable={editMode}
              isResizable={editMode}
              onLayoutChange={handleLayoutChange}
              draggableHandle=".cursor-move"
            >
              {widgets.map(widget => (
                <div key={widget.id} data-testid={`widget-${widget.id}`}>
                  <WidgetComponent
                    widget={widget}
                    data={widgetData[widget.id] || []}
                    onDelete={handleDeleteWidget}
                    editMode={editMode}
                  />
                </div>
              ))}
            </GridLayout>
          </div>
        )}

        {/* Add Widget Dialog */}
        <Dialog open={addWidgetOpen} onOpenChange={setAddWidgetOpen}>
          <DialogContent className="max-w-md w-[95vw] sm:w-full max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Adicionar Widget</DialogTitle>
              <DialogDescription>
                Configure um novo widget para o dashboard
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Título</Label>
                <Input
                  placeholder="Ex: Vendas por Mês"
                  value={newWidget.title}
                  onChange={(e) => setNewWidget({ ...newWidget, title: e.target.value })}
                  data-testid="widget-title-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Tipo de Visualização</Label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {widgetTypes.map(type => (
                    <button
                      key={type.value}
                      onClick={() => setNewWidget({ ...newWidget, type: type.value })}
                      className={`p-3 rounded-lg border text-center transition-all ${
                        newWidget.type === type.value 
                          ? 'border-indigo-500 bg-indigo-50 text-indigo-600' 
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <type.icon className="w-5 h-5 mx-auto mb-1 shrink-0" />
                      <span className="text-xs truncate block w-full">{type.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Extração de Dados</Label>
                <Select 
                  value={newWidget.extraction_id}
                  onValueChange={(value) => setNewWidget({ ...newWidget, extraction_id: value })}
                >
                  <SelectTrigger data-testid="widget-extraction-select">
                    <SelectValue placeholder="Selecione uma extração" />
                  </SelectTrigger>
                  <SelectContent>
                    {extractions.map(ext => (
                      <SelectItem key={ext.id} value={ext.id}>
                        {ext.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {extractions.length === 0 && (
                  <p className="text-xs text-slate-500 mt-1">
                    Nenhuma extração disponível. <a href="/extractions" className="text-indigo-600 hover:underline">Criar extração</a>
                  </p>
                )}
              </div>
            </div>
            
            <DialogFooter className="gap-2 sm:gap-0 mt-2">
              <Button 
                variant="outline" 
                className="w-full sm:w-auto"
                onClick={() => setAddWidgetOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleAddWidget}
                disabled={!newWidget.title || !newWidget.extraction_id}
                className="bg-indigo-600 hover:bg-indigo-700 w-full sm:w-auto"
                data-testid="confirm-add-widget-btn"
              >
                Adicionar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default DashboardBuilderPage;
